import { useOrders } from "@/contexts/OrderContext";
import { useProducts } from "@/contexts/ProductContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { db } from '@/firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";

const statusOptions = [
  "confirmed",
  "pending",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
  "completed"
];

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  completed: "bg-green-200 text-green-900"
};

export default function AdminOrders() {
  const { orders, loading, error, updateOrderStatus } = useOrders();
  const { products } = useProducts();
  const [updating, setUpdating] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("confirmed");
  const { toast } = useToast();

  const statusOptions = [
    { value: "all", label: "All" },
    { value: "confirmed", label: "Confirmed" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
    { value: "cancelled", label: "Cancelled" },
    { value: "completed", label: "Completed" },
  ];

  const getProduct = (productId: string) => products.find(p => p.id === productId);
  const getProductName = (productId: string, fallback?: string) => {
    const found = getProduct(productId);
    return found ? found.title : fallback || productId;
  };
  const getProductImage = (productId: string) => {
    const found = getProduct(productId);
    return found ? found.image : "/placeholder.svg";
  };

  const handleStatusChange = async (orderId: string, status: string) => {
    setUpdating(orderId);
    try {
      await updateOrderStatus(orderId, status);
      toast({
        title: "Order Status Updated",
        description: `Order status changed to '${status}'.`,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: "Error Updating Status",
        description: error?.message || "Failed to update order status.",
        variant: "destructive",
      });
    } finally {
      setUpdating(null);
    }
  };

  // Filter orders by orderId, product name, and status
  const filteredOrders = orders.filter(order => {
    const searchTerm = search.trim().toLowerCase();
    const matchesOrderId = searchTerm
      ? order.id.toLowerCase().includes(searchTerm)
      : true;
    const matchesProductName = searchTerm
      ? order.items.some(item => {
          const productName = getProductName(item.productId, item.name).toLowerCase();
          return productName.includes(searchTerm);
        })
      : true;
    const matchesStatus = statusFilter === "all" ? true : order.status === statusFilter;
    return (matchesOrderId || matchesProductName) && matchesStatus;
  });

  if (loading) return <div className="p-8 text-center text-lg text-gray-600">Loading orders...</div>;
  if (error) return <div className="p-8 text-center text-red-500 font-semibold">{error}</div>;

  return (
    <div className="w-full space-y-4 sm:space-y-6 p-4 sm:p-6">
      <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">All Orders</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage and track customer orders</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
          <Input
            type="search"
            placeholder="Search by Order ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full sm:w-64 lg:w-80 text-sm sm:text-base"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40 lg:w-48 text-sm sm:text-base">
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map(opt => (
                <SelectItem key={opt.value.toString()} value={opt.value.toString()}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {filteredOrders.length === 0 ? (
        <div className="text-center text-gray-500 py-8 text-sm sm:text-base">No orders found.</div>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {filteredOrders.map(order => (
            <Card key={order.id} className="overflow-hidden">
              <CardHeader className="bg-gray-50 flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-start sm:justify-between p-4 sm:p-6">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base sm:text-lg truncate">Order #{order.id.substring(0, 8).toUpperCase()}</CardTitle>
                  <CardDescription className="mt-1 text-xs sm:text-sm">
                    Placed on {(() => {
                      try {
                        const raw = order.createdAt;
                        if (!raw) return 'Unknown Date';
                        
                        if (typeof raw === 'object' && raw !== null && 'seconds' in raw && typeof (raw as any).seconds === 'number') {
                          return new Date((raw as any).seconds * 1000).toLocaleDateString();
                        } else if (typeof raw === 'string') {
                          const parsed = new Date(raw);
                          return !isNaN(parsed.getTime()) ? parsed.toLocaleDateString() : 'Unknown Date';
                        }
                        return 'Unknown Date';
                      } catch {
                        return 'Unknown Date';
                      }
                    })()}
                  </CardDescription>
                  {order.user && (
                    <div className="mt-2 text-xs sm:text-sm text-gray-700 truncate">
                      <span className="font-medium">Customer:</span> {order.user.name}
                      <span className="hidden sm:inline"> ({order.user.email})</span>
                    </div>
                  )}
                  {order.userId && !order.user && (
                    <div className="mt-2 text-xs sm:text-sm text-gray-600 truncate">User ID: {order.userId}</div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
                  <Badge className={`capitalize text-xs ${statusColors[order.status] || ''} flex-shrink-0`}>{order.status}</Badge>
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <select
                      value={order.status}
                      onChange={e => handleStatusChange(order.id, e.target.value)}
                      disabled={updating === order.id}
                      className="border rounded px-2 py-1 text-xs sm:text-sm w-full sm:w-auto min-w-[120px] btn-touch"
                    >
                      {["confirmed","pending","processing","shipped","delivered","cancelled","completed"].map(opt => (
                        <option key={opt} value={opt} className="capitalize">{opt}</option>
                      ))}
                    </select>
                    {updating === order.id && <span className="text-xs text-gray-400 flex-shrink-0">Updating...</span>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Mobile Card Layout */}
                <div className="block sm:hidden">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="border-b border-gray-200 p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <img 
                          src={getProductImage(item.productId)} 
                          alt={getProductName(item.productId, item.name)} 
                          className="h-12 w-12 object-cover rounded flex-shrink-0" 
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm truncate">{getProductName(item.productId, item.name)}</h4>
                          <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-500">Price:</span>
                          <span className="ml-1 font-medium">₹{item.price.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">GST:</span>
                          <span className="ml-1">{item.gst ? `${item.gst}%` : "—"}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Shipping:</span>
                          <span className="ml-1">{item.shippingFee ? `₹${item.shippingFee.toFixed(2)}` : "—"}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Total:</span>
                          <span className="ml-1 font-bold">₹{((item.price + (item.price * (item.gst || 0) / 100) + (item.shippingFee || 0)) * item.quantity).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Desktop Table Layout */}
                <div className="hidden sm:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[200px]">Product</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-right">GST</TableHead>
                        <TableHead className="text-right">Shipping</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {order.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <img 
                                src={getProductImage(item.productId)} 
                                alt={getProductName(item.productId, item.name)} 
                                className="h-10 w-10 object-cover rounded flex-shrink-0" 
                              />
                              <span className="text-sm truncate">{getProductName(item.productId, item.name)}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-sm">₹{item.price.toFixed(2)}</TableCell>
                          <TableCell className="text-right text-sm">
                            {item.gst ? `${item.gst}% (₹${((item.price * item.gst / 100) * item.quantity).toFixed(2)})` : "—"}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {item.shippingFee ? `₹${(item.shippingFee * item.quantity).toFixed(2)}` : "—"}
                          </TableCell>
                          <TableCell className="text-right text-sm">{item.quantity}</TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            ₹{((item.price + (item.price * (item.gst || 0) / 100) + (item.shippingFee || 0)) * item.quantity).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="p-4 sm:p-6 flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-start sm:justify-between border-t bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm sm:text-base mb-2">Shipping Address:</div>
                    <div className="text-xs sm:text-sm text-gray-700 break-words">
                      {[
                        order.shippingAddress?.name,
                        order.shippingAddress?.street,
                        order.shippingAddress?.city,
                        order.shippingAddress?.state,
                        order.shippingAddress?.zip,
                        order.shippingAddress?.country
                      ].filter(Boolean).join(', ') || 'No address provided'}
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="font-bold text-base sm:text-lg text-green-600">
                      Order Total: ₹{order.items.reduce((sum, item) =>
                        sum + ((item.price + (item.price * (item.gst || 0) / 100) + (item.shippingFee || 0)) * item.quantity)
                      , 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
} 